import { Fragment, useEffect, useRef, useState } from 'react'
import { EChartsType, format, init, use } from 'echarts/core'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { LineChart } from 'echarts/charts'
import { SVGRenderer } from 'echarts/renderers'
import { useOutsideClick } from '../../hooks'
import {
  captureScreenshot,
  formatNum,
  manageEntryTotals,
  manageEntryTotalsNoDate,
  parseDate,
  toggleExpandContextMenu
} from '../../utils/functions'
import { ContextSchema, FormSchema, QuestionSchema } from '../../utils/types/form'
import { SubmissionSchema } from '../../utils/types/submission'
import { lineChartDemoInfo, lineChartDemoX, lineChartDemoY } from '../../data/demo'

const isDemo = process.env.REACT_APP_DEMO === 'true'

const BOUNDARY_GAP_SET = '5%'
const BOUNDARY_GAP_UNSET = '0%'

// TODO cumulative entries
// TODO averages
// TODO default to entries if no date questions

export const LineGraph: React.FC<{
  form: FormSchema | null
  submissions: SubmissionSchema[]
  questions: {
    allQuestions: QuestionSchema[]
    infoQuestions: QuestionSchema[]
  }
  plotQuestions: {
    plotNumQuestions: QuestionSchema[]
    plotDateQuestions: QuestionSchema[]
    plotCatQuestions: QuestionSchema[]
  }
  filters: {
    filterOptions: Map<string, Set<string>>
    isFilterOptionsOpen: boolean
    setIsFilterOptionsOpen: React.Dispatch<React.SetStateAction<boolean>>
    filterOptionsTriggerRef: React.RefObject<null>
    filterOptionsContextRef: React.RefObject<null>
    handleFilterOptionsChange: Function
    getIsFilterOptionsAllSelected: Function
    isFilterOptionsAllSelected: boolean
  }
  theme: { name: string; hex: string }
}> = (props) => {
  const { form, submissions, questions, plotQuestions, filters, theme } = props
  const { allQuestions, infoQuestions } = questions
  const { plotNumQuestions, plotDateQuestions, plotCatQuestions } = plotQuestions
  const {
    filterOptions,
    isFilterOptionsOpen,
    setIsFilterOptionsOpen,
    filterOptionsTriggerRef,
    filterOptionsContextRef,
    handleFilterOptionsChange,
    getIsFilterOptionsAllSelected,
    isFilterOptionsAllSelected
  } = filters

  use([GridComponent, LineChart, SVGRenderer, TooltipComponent])

  const [isPlotNumOptionsOpen, setIsPlotNumOptionsOpen] = useState(false)
  const [plotNumQuestionId, setPlotNumQuestionId] = useState('')
  const [plotNumQuestion, setPlotNumQuestion] = useState<QuestionSchema | null>(null)
  const [isPlotDateOptionsOpen, setIsPlotDateOptionsOpen] = useState(false)
  const [plotDateQuestionId, setPlotDateQuestionId] = useState('by_date_of_submission')
  const [plotDateQuestion, setPlotDateQuestion] = useState<QuestionSchema | null>(null)
  const [infoQuestionId, setInfoQuestionId] = useState('')
  const [isGraphInfoContextMenuOpen, setIsGraphInfoContextMenuOpen] = useState(false)
  const [isGraphOptionsContextMenuOpen, setIsGraphOptionsContextMenuOpen] = useState(false)
  const [isContextMenuExpanded, setIsContextMenuExpanded] = useState<Record<string, boolean>>({})
  const [contextPerDate, setContextPerDate] = useState<Map<number, SubmissionSchema[]>>(new Map())
  const [graphOptions, setGraphOptions] = useState<{
    isCumulative: boolean
    hasYBoundaryGap: string[]
    hasXBoundaryGap: string[] | boolean
    hasShadedArea: boolean
    isLineSmooth: boolean
    isLineStepped: boolean
  }>({
    isCumulative: false,
    hasYBoundaryGap: [BOUNDARY_GAP_UNSET, BOUNDARY_GAP_UNSET],
    hasXBoundaryGap: false,
    hasShadedArea: false,
    isLineSmooth: true,
    isLineStepped: false
  })
  const [axisMap, setAxisMap] = useState(new Map())
  const maxValue = useRef(0)
  const chart = useRef<EChartsType>(null)
  const domElement = useRef(null)
  const graphRef = useRef(null)
  const plotNumQuestionsTriggerRef = useRef(null)
  const plotNumQuestionsContextRef = useRef(null)
  const plotDateQuestionsTriggerRef = useRef(null)
  const plotDateQuestionsContextRef = useRef(null)
  const graphInfoTriggerRef = useRef(null)
  const graphInfoContextRef = useRef(null)
  const graphOptionsTriggerRef = useRef(null)
  const graphOptionsContextRef = useRef(null)

  useOutsideClick(
    {
      element: plotNumQuestionsContextRef,
      trigger: plotNumQuestionsTriggerRef
    },
    () => setIsPlotNumOptionsOpen(false)
  )

  useOutsideClick(
    {
      element: plotDateQuestionsContextRef,
      trigger: plotDateQuestionsTriggerRef
    },
    () => setIsPlotDateOptionsOpen(false)
  )

  useOutsideClick({ element: graphInfoContextRef, trigger: graphInfoTriggerRef }, () =>
    setIsGraphInfoContextMenuOpen(false)
  )

  useOutsideClick({ element: graphOptionsContextRef, trigger: graphOptionsTriggerRef }, () =>
    setIsGraphOptionsContextMenuOpen(false)
  )

  useEffect(() => {
    chart.current = init(domElement.current, null, { renderer: 'svg' })

    document.addEventListener('keyup', (e) => captureScreenshot(e, graphRef, []))

    return () => {
      document.removeEventListener('keyup', (e) => captureScreenshot(e, graphRef, []))
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    plotNumQuestions.length && setPlotNumQuestionId(plotNumQuestions[0].id)
  }, [plotNumQuestions])

  useEffect(() => {
    plotDateQuestions.length && setPlotDateQuestionId(plotDateQuestions[0].id)
  }, [plotDateQuestions])

  useEffect(() => {
    infoQuestions.length && handleInfoQuestionChange(getInfoQuestion())
    // eslint-disable-next-line
  }, [infoQuestions, plotDateQuestionId])

  useEffect(() => {
    const question = allQuestions.find(({ id }) => id === plotNumQuestionId)
    setPlotNumQuestion(question ?? null)

    // eslint-disable-next-line
  }, [plotNumQuestionId])

  useEffect(() => {
    const question = allQuestions.find(({ id }) => id === plotDateQuestionId)
    setPlotDateQuestion(question ?? null)

    // eslint-disable-next-line
  }, [plotDateQuestionId])

  useEffect(() => {
    const allValues: (number | number[])[] = []
    const allLabels: (number | string)[] = []
    let entriesPerDate = new Map<number, number>()
    let contextPerDate = new Map<number, SubmissionSchema[]>()

    maxValue.current = 0
    let entryCounter = 1

    if (plotNumQuestionId) {
      submissions.forEach((submission, submissionIndex) => {
        let globalSectionIndex = -1
        let globalQuestionIndex = -1

        submissions[submissionIndex].sections.forEach(({ questions }, sIndex) => {
          questions.forEach(({ id }, qIndex) => {
            if (id === plotDateQuestionId) {
              globalSectionIndex = sIndex
              globalQuestionIndex = qIndex
            }
          })
        })

        const dateAnswer =
          submissions[submissionIndex].sections[globalSectionIndex]?.questions[globalQuestionIndex]
            .answer

        submission.sections.forEach(({ questions }, sectionIndex) => {
          questions
            .filter(({ id }) => id === plotNumQuestionId)
            .filter(() => {
              let disqualify = false

              for (const question of plotCatQuestions) {
                const catQuestionIndex = submissions[submissionIndex].sections[
                  sectionIndex
                ].questions.findIndex(({ id }) => id === question.id)

                const catQuestion =
                  submissions[submissionIndex].sections[sectionIndex].questions[catQuestionIndex]

                if (catQuestion) {
                  const catAnswer = catQuestion.answer
                  const optionsSet = filterOptions.get(question.id)

                  if (optionsSet) {
                    if (catQuestion.type === 'dropdown') {
                      if (!optionsSet.has(catAnswer as string)) {
                        disqualify = true
                        break
                      }
                    }

                    if (catQuestion.type === 'checkbox' && Array.isArray(catAnswer)) {
                      let isAnswerInSet = false

                      for (const answer of catAnswer) {
                        if (optionsSet.has(answer)) {
                          isAnswerInSet = true
                          break
                        }
                      }

                      if (!isAnswerInSet) {
                        disqualify = true
                        break
                      }
                    }
                  }
                }
              }

              return !disqualify
            })
            .forEach(({ answer }) => {
              let baseValue = Number(answer)
              let multiplier = 0

              for (const question of plotCatQuestions) {
                if (question.type === 'checkbox') {
                  const catQuestionIndex = submissions[submissionIndex].sections[
                    sectionIndex
                  ].questions.findIndex(({ id }) => id === question.id)

                  const catQuestion =
                    submissions[submissionIndex].sections[sectionIndex].questions[catQuestionIndex]

                  const catAnswer = catQuestion?.answer

                  if (Array.isArray(catAnswer)) {
                    baseValue /= catAnswer.length

                    const options = filterOptions.get(catQuestion.id)

                    for (const answer of catAnswer) {
                      if (options?.has(answer)) {
                        multiplier++
                      }
                    }
                  }
                }
              }

              if (!multiplier) {
                multiplier = 1
              }

              answer = baseValue * multiplier
              maxValue.current = maxValue.current + answer

              if (plotDateQuestionId) {
                if (plotDateQuestionId === 'by_date_of_submission') {
                  ;({ entriesPerDate, contextPerDate } = manageEntryTotals({
                    entriesPerDate,
                    contextPerDate,
                    submission,
                    date: submission.submittedOn,
                    answer: graphOptions.isCumulative ? maxValue.current : Number(answer),
                    isCumulative: graphOptions.isCumulative
                  }))

                  allLabels.push(submission.submittedOn)
                } else {
                  ;({ entriesPerDate, contextPerDate } = manageEntryTotals({
                    entriesPerDate,
                    contextPerDate,
                    submission,
                    date: Number(dateAnswer),
                    answer: graphOptions.isCumulative ? maxValue.current : Number(answer),
                    isCumulative: graphOptions.isCumulative
                  }))

                  allLabels.push(Number(dateAnswer))
                }
              } else {
                ;({ contextPerDate } = manageEntryTotalsNoDate({
                  contextPerDate,
                  submission,
                  answer: entryCounter
                }))

                allLabels.push(`Entry ${entryCounter++}`)
                allValues.push(graphOptions.isCumulative ? maxValue.current : Number(answer))
              }
            })
        })
      })

      entriesPerDate.forEach((entries, date) => {
        allValues.push([date, entries])
      })
    } else {
      if (plotDateQuestionId === 'by_date_of_submission') {
        submissions.forEach((submission) => {
          ;({ entriesPerDate, contextPerDate } = manageEntryTotals({
            entriesPerDate,
            contextPerDate,
            submission,
            date: submission.submittedOn,
            answer: 1
          }))
        })

        entriesPerDate.forEach((entries, date) => {
          allValues.push([date, entries])
        })
      } else {
        submissions.forEach((submission) => {
          submission.sections.forEach(({ questions }) => {
            questions
              .filter(({ id }) => id === plotDateQuestionId)
              .forEach(({ answer }) => {
                ;({ entriesPerDate, contextPerDate } = manageEntryTotals({
                  entriesPerDate,
                  contextPerDate,
                  submission,
                  date: Number(answer),
                  answer: 1
                }))
              })
          })
        })

        entriesPerDate.forEach((entries, date) => {
          allValues.push([date, entries])
        })
      }
    }

    setContextPerDate(contextPerDate)

    setAxisMap((axisMap) => {
      const newMap = new Map(axisMap)
      newMap.set('yAxis', allValues)
      newMap.set('xAxis', allLabels)
      return newMap
    })

    // eslint-disable-next-line
  }, [submissions, plotNumQuestionId, plotDateQuestionId, filterOptions, graphOptions.isCumulative])

  useEffect(() => {
    const options = {
      grid: { top: '0%', right: '0%', bottom: '0%', left: '0%' },
      xAxis: {
        type: plotDateQuestionId ? 'time' : 'category',
        name: plotDateQuestionId ? 'Dates' : 'Entries',
        nameTextStyle: { fontFamily: 'Archivo', fontWeight: 'bold' },
        boundaryGap: graphOptions.hasXBoundaryGap,
        data: isDemo ? lineChartDemoX : !plotDateQuestionId && axisMap.get('xAxis'),
        axisTick: { show: true, alignWithLabel: true },
        axisLabel: {
          fontFamily: 'Archivo',
          hideOverlap: true,
          formatter: (value: number) => (plotDateQuestionId ? parseDate(value, 'min_date') : value)
        }
      },
      yAxis: {
        type: 'value',
        name: `Units: ${plotNumQuestion?.meta?.numberOptions?.unit ? plotNumQuestion.meta.numberOptions.unit : 'Values'}`,
        nameTextStyle: {
          fontFamily: 'Archivo',
          fontWeight: 'bold'
        },
        boundaryGap: graphOptions.hasYBoundaryGap,
        nameGap: 20,
        offset: 5,
        splitLine: {
          show: true,
          lineStyle: { type: 'dashed' }
        }
      },
      tooltip: {
        className: 'graph_tooltip',
        trigger: 'axis',
        enterable: true,
        formatter: (params: any) => {
          const { data, dataIndex, value } = params[0]

          let primaryValue = ''
          const unit = plotNumQuestion?.meta?.numberOptions?.unit

          if (Array.isArray(value)) {
            primaryValue = formatNum(value[1])
          } else {
            primaryValue = formatNum(value)
          }

          if (unit) {
            primaryValue = `${primaryValue} ${unit}`
          }

          const dateSubmissions = contextPerDate.get(Array.isArray(value) ? data[0] : dataIndex + 1)
          const allQuestionsContext: ContextSchema[] = []
          let questionContext: ContextSchema
          let demoInfoCounter = 0

          if (dateSubmissions) {
            dateSubmissions.forEach(({ sections }) => {
              sections.forEach(({ questions }) => {
                questions.forEach(({ id, answer }) => {
                  if (isDemo && id === infoQuestionId) {
                    questionContext = {
                      ...questionContext,
                      context: lineChartDemoInfo[demoInfoCounter]?.[0],
                      value: lineChartDemoInfo[demoInfoCounter]?.[1] as number
                    }
                    demoInfoCounter++
                  } else {
                    if (id === infoQuestionId) {
                      questionContext = {
                        ...questionContext,
                        context: answer ?? 'N/A'
                      }
                    }

                    if (id === plotNumQuestionId && typeof answer === 'number') {
                      questionContext = { ...questionContext, value: answer }
                    }
                  }
                })
              })

              allQuestionsContext.push(questionContext)
            })
          }

          const htmlContent = allQuestionsContext.map((question) => {
            if (question) {
              const contextStr = Array.isArray(question.context)
                ? question.context.join(', ')
                : typeof question.context === 'number'
                  ? parseDate(question.context, 'date')
                  : typeof question.context === 'boolean'
                    ? question.context
                      ? 'Yes'
                      : 'N/A'
                    : question.context?.toString() || 'N/A'

              let valueStr = question.value ? formatNum(question.value) : ''
              const unit = plotNumQuestion?.meta?.numberOptions?.unit

              if (valueStr && unit) {
                valueStr = `${valueStr} ${unit}`
              }

              return `
                <span ${!valueStr && `style="grid-column: 1/3"`}>${format.encodeHTML(contextStr)}</span>
                ${valueStr && `<span>${format.encodeHTML(valueStr)}</span>`}
              `
            } else {
              return `
                <span style="grid-column: 1/3">N/A</span>
              `
            }
          })

          if (Array.isArray(value)) {
            return `
              <span class="graph_tooltip-header">${format.encodeHTML(parseDate(value[0], 'date'))}</span>
              ${
                primaryValue &&
                `
                  <br />
                  <span style="color: ${format.encodeHTML(theme.hex)}">${format.encodeHTML(primaryValue)}</span>
                  ${
                    infoQuestionId &&
                    `<div class="graph_tooltip-context">${htmlContent.join('')}</div>`
                  }
                `
              }
            `
          } else {
            return `
              <span class="graph_tooltip-header">Entry ${format.encodeHTML(dataIndex + 1)}</span>
              ${
                primaryValue &&
                `
                <br />
                <span style="color: ${format.encodeHTML(theme.hex)}">${primaryValue ? format.encodeHTML(primaryValue) : '-'}</span>
                ${
                  infoQuestionId &&
                  `<div class="graph_tooltip-context">${htmlContent.join('')}</div>`
                }
              `
              }
            `
          }
        }
      },
      series: [
        {
          type: 'line',
          data: isDemo ? lineChartDemoY : axisMap.get('yAxis'),
          itemStyle: { color: theme.hex },
          lineStyle: { color: theme.hex },
          areaStyle: graphOptions.hasShadedArea
            ? {
                opacity: 0.7,
                color: {
                  type: 'linear',
                  x: 0,
                  x2: 0,
                  y: 0,
                  y2: 1,
                  colorStops: [
                    {
                      offset: 0,
                      color: `${theme.hex}ff`
                    },
                    {
                      offset: 1,
                      color: `${theme.hex}00`
                    }
                  ],
                  global: false
                }
              }
            : null,
          smooth: graphOptions.isLineSmooth && 0.4,
          step: graphOptions.isLineStepped && 'end'
        }
      ]
    }

    options && chart.current && chart.current.setOption(options)

    // eslint-disable-next-line
  }, [
    axisMap,
    infoQuestionId,
    graphOptions.hasYBoundaryGap,
    graphOptions.hasXBoundaryGap,
    graphOptions.hasShadedArea,
    graphOptions.isLineSmooth,
    graphOptions.isLineStepped
  ])

  useEffect(() => {
    // Manage hasXBoundaryGap types based on plotDateQuestionId value
    if (plotDateQuestionId) {
      setGraphOptions({
        ...graphOptions,

        hasXBoundaryGap:
          typeof graphOptions.hasXBoundaryGap === 'object'
            ? graphOptions.hasXBoundaryGap[0] === BOUNDARY_GAP_SET &&
              graphOptions.hasXBoundaryGap[1] === BOUNDARY_GAP_SET
              ? [BOUNDARY_GAP_SET, BOUNDARY_GAP_SET]
              : [BOUNDARY_GAP_UNSET, BOUNDARY_GAP_UNSET]
            : graphOptions.hasXBoundaryGap
              ? [BOUNDARY_GAP_SET, BOUNDARY_GAP_SET]
              : [BOUNDARY_GAP_UNSET, BOUNDARY_GAP_UNSET]
      })
    } else {
      if (Array.isArray(graphOptions.hasXBoundaryGap)) {
        const hasGap =
          graphOptions.hasXBoundaryGap[0] === BOUNDARY_GAP_SET &&
          graphOptions.hasXBoundaryGap[1] === BOUNDARY_GAP_SET

        setGraphOptions({ ...graphOptions, hasXBoundaryGap: hasGap })
      }
    }
    // eslint-disable-next-line
  }, [plotDateQuestionId])

  const handlePlotNumQuestionChange = (questionId: string) => setPlotNumQuestionId(questionId)

  const handlePlotDateQuestionChange = (questionId: string) => setPlotDateQuestionId(questionId)

  const handleInfoQuestionChange = (questionId: string) => setInfoQuestionId(questionId)

  const handleOptionsCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.id === 'hasXBoundaryGap') {
      plotDateQuestionId
        ? setGraphOptions({
            ...graphOptions,
            [e.target.id]: e.target.checked
              ? [BOUNDARY_GAP_SET, BOUNDARY_GAP_SET]
              : [BOUNDARY_GAP_UNSET, BOUNDARY_GAP_UNSET]
          })
        : setGraphOptions({ ...graphOptions, [e.target.id]: e.target.checked })
    } else if (e.target.id === 'hasYBoundaryGap') {
      setGraphOptions({
        ...graphOptions,
        [e.target.id]: e.target.checked
          ? [BOUNDARY_GAP_SET, BOUNDARY_GAP_SET]
          : [BOUNDARY_GAP_UNSET, BOUNDARY_GAP_UNSET]
      })
    } else {
      setGraphOptions({ ...graphOptions, [e.target.id]: e.target.checked })
    }
  }

  const handleExpandContextMenu = (ref: HTMLDivElement, wrapped: boolean = false) => {
    toggleExpandContextMenu(ref, isContextMenuExpanded, setIsContextMenuExpanded, wrapped)
  }

  const getInfoQuestion = () => {
    for (const { id } of infoQuestions) {
      if (id !== plotDateQuestionId) {
        return id
      }
    }

    return ''
  }

  return (
    <div className='view_tab_content' ref={graphRef}>
      <div className='actions_container'>
        <div className='actions-select actions-select-fg'>
          <span className='material-symbols-rounded'>line_axis</span>
          <div className='actions_series'>
            <div>
              <div
                id='plot_num_questions_trigger'
                className='div-with-icon'
                onClick={() => setIsPlotNumOptionsOpen(!isPlotNumOptionsOpen)}
                ref={plotNumQuestionsTriggerRef}>
                <span id='plot_num_questions_trigger'>Y-axis</span>
                <span id='plot_num_questions_trigger' className='material-symbols-rounded'>
                  {isPlotNumOptionsOpen ? 'arrow_drop_down' : 'arrow_right'}
                </span>
              </div>
              {isPlotNumOptionsOpen && (
                <div
                  className={`context_menu context_menu-left context_menu-down context_menu-${
                    isContextMenuExpanded['plotNumQuestionsContext'] ? 'lg' : 'md'
                  }`}
                  id='plotNumQuestionsContext'
                  ref={plotNumQuestionsContextRef}>
                  <div className='context_menu-grid context-header context-header-defaults'>
                    <div
                      onClick={() =>
                        plotNumQuestionsContextRef.current &&
                        handleExpandContextMenu(plotNumQuestionsContextRef.current)
                      }>
                      <h4>Values</h4>
                      <span className='material-symbols-rounded'>
                        {isContextMenuExpanded['plotNumQuestionsContext']
                          ? 'collapse_content'
                          : 'expand_content'}
                      </span>
                    </div>
                    {plotDateQuestionId && (
                      <>
                        <input
                          id='checkbox_entries'
                          type='checkbox'
                          checked={!plotNumQuestionId}
                          onChange={() => handlePlotNumQuestionChange('')}
                        />
                        <label htmlFor='checkbox_entries' className='highlight-styled-hover'>
                          Entries
                        </label>
                      </>
                    )}
                  </div>
                  {!!plotNumQuestions.length && (
                    <>
                      {form?.sections.map(({ id: sectionId, name, questions }) => {
                        const hasQuestions = questions.some(({ type }) => type === 'number')

                        let numericOptions = false

                        for (const question of questions) {
                          const isEveryOptionNumeric = question.meta?.dropdownOptions?.values.every(
                            (option) => !Number.isNaN(Number(option))
                          )

                          if (isEveryOptionNumeric) {
                            numericOptions = true
                            break
                          }
                        }

                        if (!hasQuestions && !numericOptions) {
                          return <Fragment key={sectionId} />
                        }

                        return (
                          <div className='context_menu-grouped' key={sectionId}>
                            <h5>{name}</h5>
                            <div className='context_menu-grid'>
                              {questions.map(({ id: questionId, type, statement }) => {
                                if (type === 'number' || (type === 'dropdown' && numericOptions)) {
                                  return (
                                    <Fragment key={questionId}>
                                      <input
                                        id={`checkbox_${questionId}`}
                                        type='checkbox'
                                        checked={plotNumQuestionId === questionId}
                                        onChange={() => handlePlotNumQuestionChange(questionId)}
                                      />
                                      <label
                                        htmlFor={`checkbox_${questionId}`}
                                        className='highlight-styled-hover'>
                                        {statement}
                                      </label>
                                    </Fragment>
                                  )
                                } else {
                                  return <Fragment key={questionId} />
                                }
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          {isDemo ? (
            <span className='actions-active'>Q1 expenditure</span>
          ) : plotNumQuestion ? (
            <span
              className='actions-active'
              title={`${plotNumQuestion.statement}${
                plotNumQuestion.description ? ' (' + plotNumQuestion.description + ')' : ''
              }`}>
              {plotNumQuestion.statement}
              {plotNumQuestion.meta?.numberOptions?.unit &&
                ` (${plotNumQuestion.meta.numberOptions.unit})`}
            </span>
          ) : (
            <span className='actions-active'>Entries</span>
          )}
        </div>
        <div className='actions-select actions-select-filter'>
          <div>
            <span className='material-symbols-rounded material-symbols-sm'>event_list</span>
            {!isFilterOptionsAllSelected && (
              <span className='on-indicator' title='Some filters have been applied'>
                ●
              </span>
            )}
          </div>
          <div className='actions_series'>
            <div>
              <div
                id='plot_cat_questions_trigger'
                className='div-with-icon'
                onClick={() => setIsFilterOptionsOpen(!isFilterOptionsOpen)}
                ref={filterOptionsTriggerRef}>
                <span id='plot_cat_questions_trigger'>Show filters</span>
                <span id='plot_cat_questions_trigger' className='material-symbols-rounded'>
                  {isFilterOptionsOpen ? 'arrow_drop_down' : 'arrow_right'}
                </span>
              </div>
              {isFilterOptionsOpen && (
                <div
                  className={`context_menu context_menu-down context_menu-${
                    isContextMenuExpanded['filterOptionsContext'] ? 'lg' : 'md'
                  }`}
                  id='filterOptionsContext'
                  ref={filterOptionsContextRef}>
                  <div className='context_menu-grid context-header'>
                    <div
                      onClick={() =>
                        filterOptionsContextRef.current &&
                        handleExpandContextMenu(filterOptionsContextRef.current)
                      }>
                      <h4>Values</h4>
                      <span className='material-symbols-rounded'>
                        {isContextMenuExpanded['filterOptionsContext']
                          ? 'collapse_content'
                          : 'expand_content'}
                      </span>
                    </div>
                  </div>
                  {plotCatQuestions.length ? (
                    <>
                      {form?.sections.map(({ id: sectionId, name, questions }) => {
                        const hasQuestions = questions.some(
                          ({ type }) => type === 'dropdown' || type === 'checkbox'
                        )

                        if (!hasQuestions) {
                          return <Fragment key={sectionId} />
                        }

                        return (
                          <div className='context_menu-grouped' key={sectionId}>
                            <h5>{name}</h5>
                            {questions.map(({ id: questionId, type, statement, meta }) => {
                              if (type === 'dropdown' || type === 'checkbox') {
                                return (
                                  <div className='context_menu-grouped' key={questionId}>
                                    <span>{statement}</span>
                                    <div className='context_menu-grid'>
                                      <input
                                        id={`checkbox_${questionId}_all`}
                                        type='checkbox'
                                        checked={getIsFilterOptionsAllSelected(questionId)}
                                        onChange={() => handleFilterOptionsChange(questionId, '')}
                                      />
                                      <label
                                        htmlFor={`checkbox_${questionId}_all`}
                                        className='highlight-styled-hover'>
                                        All
                                      </label>
                                      {type === 'dropdown' &&
                                        meta?.dropdownOptions?.values.map((option, index) => (
                                          <Fragment key={index}>
                                            <input
                                              id={`checkbox_${questionId}_${option}`}
                                              type='checkbox'
                                              checked={filterOptions.get(questionId)?.has(option)}
                                              onChange={() =>
                                                handleFilterOptionsChange(questionId, option)
                                              }
                                            />
                                            <label
                                              htmlFor={`checkbox_${questionId}_${option}`}
                                              className='highlight-styled-hover'>
                                              {option}
                                            </label>
                                          </Fragment>
                                        ))}
                                      {type === 'checkbox' &&
                                        meta?.checkboxOptions?.values.map((option, index) => (
                                          <Fragment key={index}>
                                            <input
                                              id={`checkbox_${questionId}_${option}`}
                                              type='checkbox'
                                              checked={filterOptions.get(questionId)?.has(option)}
                                              onChange={() =>
                                                handleFilterOptionsChange(questionId, option)
                                              }
                                            />
                                            <label
                                              htmlFor={`checkbox_${questionId}_${option}`}
                                              className='highlight-styled-hover'>
                                              {option}
                                            </label>
                                          </Fragment>
                                        ))}
                                    </div>
                                  </div>
                                )
                              } else {
                                return <Fragment key={questionId} />
                              }
                            })}
                          </div>
                        )
                      })}
                    </>
                  ) : (
                    <span>(No filters available)</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div ref={domElement} className='graph' />
      <div className='actions_container'>
        <div className='actions-select actions-select-fg'>
          <span className='material-symbols-rounded'>sort</span>
          <div className='actions_series'>
            <div>
              <div
                id='plot_date_questions_trigger'
                className='div-with-icon'
                onClick={() => setIsPlotDateOptionsOpen(!isPlotDateOptionsOpen)}
                ref={plotDateQuestionsTriggerRef}>
                <span id='plot_date_questions_trigger'>X-axis</span>
                <span id='plot_date_questions_trigger' className='material-symbols-rounded'>
                  {isPlotDateOptionsOpen ? 'arrow_drop_up' : 'arrow_right'}
                </span>
              </div>
              {isPlotDateOptionsOpen && (
                <div
                  className={`context_menu context_menu-left context_menu-up context_menu-${
                    isContextMenuExpanded['plotDateQuestionsContext'] ? 'lg' : 'md'
                  }`}
                  id='plotDateQuestionsContext'
                  ref={plotDateQuestionsContextRef}>
                  <div className='context_menu-grid context-header context-header-defaults'>
                    <div
                      onClick={() =>
                        plotDateQuestionsContextRef.current &&
                        handleExpandContextMenu(plotDateQuestionsContextRef.current)
                      }>
                      <h4>Values</h4>
                      <span className='material-symbols-rounded'>
                        {isContextMenuExpanded['plotDateQuestionsContext']
                          ? 'collapse_content'
                          : 'expand_content'}
                      </span>
                    </div>
                    {plotNumQuestionId && (
                      <>
                        <input
                          id='checkbox_by_entry'
                          type='checkbox'
                          checked={!plotDateQuestionId}
                          onChange={() => handlePlotDateQuestionChange('')}
                        />
                        <label htmlFor='checkbox_by_entry' className='highlight-styled-hover'>
                          By entry
                        </label>
                      </>
                    )}
                    <input
                      id='checkbox_by_date_of_submission'
                      type='checkbox'
                      checked={plotDateQuestionId === 'by_date_of_submission'}
                      onChange={() => handlePlotDateQuestionChange('by_date_of_submission')}
                    />
                    <label
                      htmlFor='checkbox_by_date_of_submission'
                      className='highlight-styled-hover'>
                      By date submitted
                    </label>
                  </div>
                  {form?.sections.map(({ id: sectionId, name, questions }) => {
                    const hasQuestions = questions.some(({ type }) => type === 'date')

                    if (!hasQuestions) {
                      return <Fragment key={sectionId} />
                    }

                    return (
                      <div className='context_menu-grouped' key={sectionId}>
                        <h5>{name}</h5>
                        <div className='context_menu-grid'>
                          {questions.map(({ id: questionId, type, statement }) => {
                            if (type === 'date') {
                              return (
                                <Fragment key={questionId}>
                                  <input
                                    id={`checkbox_${questionId}`}
                                    type='checkbox'
                                    checked={plotDateQuestionId === questionId}
                                    onChange={() => handlePlotDateQuestionChange(questionId)}
                                  />
                                  <label
                                    htmlFor={`checkbox_${questionId}`}
                                    className='highlight-styled-hover'>
                                    {statement}
                                  </label>
                                </Fragment>
                              )
                            } else {
                              return <Fragment key={questionId} />
                            }
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
          {isDemo ? (
            <span className='actions-active'>Departments</span>
          ) : plotDateQuestion ? (
            <span
              className='actions-active'
              title={`${plotDateQuestion.statement}${
                plotDateQuestion.description ? ' (' + plotDateQuestion.description + ')' : ''
              }`}>
              {plotDateQuestion.statement}
            </span>
          ) : (
            <>
              {plotDateQuestionId === 'by_date_of_submission' ? (
                <span className='actions-active'>By date submitted</span>
              ) : (
                <span className='actions-active'>By entry</span>
              )}
            </>
          )}
        </div>
        <div className='graph_options'>
          {!!infoQuestions.length && (
            <div>
              <span
                id='graph_context_trigger'
                className='material-symbols-rounded material-symbols-sm'
                onClick={() => setIsGraphInfoContextMenuOpen(!isGraphInfoContextMenuOpen)}
                ref={graphInfoTriggerRef}>
                info
              </span>
              {isGraphInfoContextMenuOpen && (
                <div
                  className={`context_menu context_menu-up context_menu-${
                    isContextMenuExpanded['graphInfoContext'] ? 'lg' : 'md'
                  }`}
                  id='graphInfoContext'
                  ref={graphInfoContextRef}>
                  <div className='context_menu-grid context-header context-header-defaults'>
                    <div
                      onClick={() =>
                        graphInfoContextRef.current &&
                        handleExpandContextMenu(graphInfoContextRef.current)
                      }>
                      <h4>Values</h4>
                      <span className='material-symbols-rounded'>
                        {isContextMenuExpanded['graphInfoContext']
                          ? 'collapse_content'
                          : 'expand_content'}
                      </span>
                    </div>
                    <input
                      id='checkbox_no_info'
                      type='checkbox'
                      checked={!!infoQuestionId}
                      onChange={() =>
                        handleInfoQuestionChange(infoQuestionId ? '' : getInfoQuestion())
                      }
                    />
                    <label htmlFor='checkbox_no_info' className='highlight-styled-hover'>
                      Show info
                    </label>
                  </div>
                  {form?.sections.map(({ id: sectionId, name, questions }) => {
                    const hasQuestions = questions.some(
                      ({ id, type }) => type !== 'number' && id !== plotDateQuestionId
                    )

                    if (!hasQuestions) {
                      return <Fragment key={sectionId} />
                    }

                    return (
                      <div className='context_menu-grouped' key={sectionId}>
                        <h5>{name}</h5>
                        <div className='context_menu-grid'>
                          {questions.map(({ id: questionId, type, statement }) => {
                            if (type !== 'number' && questionId !== plotDateQuestionId) {
                              return (
                                <Fragment key={questionId}>
                                  <input
                                    id={`checkbox_${questionId}`}
                                    type='checkbox'
                                    checked={infoQuestionId === questionId}
                                    onChange={() => handleInfoQuestionChange(questionId)}
                                  />
                                  <label
                                    htmlFor={`checkbox_${questionId}`}
                                    className='highlight-styled-hover'>
                                    {statement}
                                  </label>
                                </Fragment>
                              )
                            } else {
                              return <Fragment key={questionId} />
                            }
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
          <div>
            <span
              id='graph_options_trigger'
              className='material-symbols-rounded'
              onClick={() => setIsGraphOptionsContextMenuOpen(!isGraphOptionsContextMenuOpen)}
              ref={graphOptionsTriggerRef}>
              instant_mix
            </span>
            {isGraphOptionsContextMenuOpen && (
              <div
                className={`context_menu context_menu-up context_menu-${
                  isContextMenuExpanded['graphOptionsContext'] ? 'lg' : 'wrap'
                }`}
                id='graphOptionsContext'
                ref={graphOptionsContextRef}>
                <div className='context-header'>
                  <div
                    onClick={() =>
                      graphOptionsContextRef.current &&
                      handleExpandContextMenu(graphOptionsContextRef.current, true)
                    }>
                    <h4>Values</h4>
                    <span className='material-symbols-rounded'>
                      {isContextMenuExpanded['graphOptionsContext']
                        ? 'collapse_content'
                        : 'expand_content'}
                    </span>
                  </div>
                </div>
                <div className='context_menu-grid'>
                  <input
                    id='isCumulative'
                    type='checkbox'
                    checked={graphOptions.isCumulative}
                    onChange={handleOptionsCheckboxChange}
                  />
                  <label htmlFor='isCumulative' className='highlight-styled-hover'>
                    Cumulative
                  </label>
                  <input
                    id='hasYBoundaryGap'
                    type='checkbox'
                    checked={
                      graphOptions.hasYBoundaryGap[0] === BOUNDARY_GAP_SET &&
                      graphOptions.hasYBoundaryGap[1] === BOUNDARY_GAP_SET
                    }
                    onChange={handleOptionsCheckboxChange}
                  />
                  <label htmlFor='hasYBoundaryGap' className='highlight-styled-hover'>
                    Y-axis spacing
                  </label>
                  <input
                    id='hasXBoundaryGap'
                    type='checkbox'
                    checked={
                      typeof graphOptions.hasXBoundaryGap === 'object'
                        ? graphOptions.hasXBoundaryGap[0] === BOUNDARY_GAP_SET &&
                          graphOptions.hasXBoundaryGap[1] === BOUNDARY_GAP_SET
                        : graphOptions.hasXBoundaryGap
                    }
                    onChange={handleOptionsCheckboxChange}
                  />
                  <label htmlFor='hasXBoundaryGap' className='highlight-styled-hover'>
                    X-axis spacing
                  </label>
                  <input
                    id='hasShadedArea'
                    type='checkbox'
                    checked={graphOptions.hasShadedArea}
                    onChange={handleOptionsCheckboxChange}
                  />
                  <label htmlFor='hasShadedArea' className='highlight-styled-hover'>
                    Shaded
                  </label>
                  <input
                    id='isLineSmooth'
                    type='checkbox'
                    checked={graphOptions.isLineSmooth}
                    disabled={graphOptions.isLineStepped}
                    onChange={handleOptionsCheckboxChange}
                  />
                  <label htmlFor='isLineSmooth' className='highlight-styled-hover'>
                    Smooth line
                  </label>
                  <input
                    id='isLineStepped'
                    type='checkbox'
                    checked={graphOptions.isLineStepped}
                    disabled={graphOptions.isLineSmooth}
                    onChange={handleOptionsCheckboxChange}
                  />
                  <label htmlFor='isLineStepped' className='highlight-styled-hover'>
                    Stepped line
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
